import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createDissertation, getDissertationFiles, resubmitDissertation } from "./dissertation.api";
import { Button } from "@/components/ui/button";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { dissertationSubmissionSchema, type Dissertation } from "./dissertation.schema";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { DashboardLayout } from "@/app/layout/DashboardLayout";
import { useEffect } from "react";
import useGcpsUIController from "@/hooks/useGlobalContext";
import { toaster } from "@/components/Toaster";
import { useNavigate } from "react-router-dom";
import { routes } from "@/app/routes/paths";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import { formFileDownloadByPathUrl, handleMutationError } from "@/lib/utils";
import { ArrowDownToLine } from "lucide-react";

type DissertationSubmissionFormProps = {
	dissertation?: Dissertation | null;
};

type DissertationFormValues = {
	title: string;
	abstract: string;
	dissertation_file?: File;
	dissertation_docx?: File;
	proposal_file?: File;
	cover_letter_file?: File;
	turnitin_score_file?: File;
};

const REQUIRED_FILE_FIELDS: Array<{
	name: keyof DissertationFormValues;
	message: string;
}> = [
	{ name: "dissertation_file", message: "Dissertation PDF file is required" },
	{ name: "dissertation_docx", message: "Dissertation Word file is required" },
	{ name: "proposal_file", message: "Proposal PDF file is required" },
	{ name: "cover_letter_file", message: "Cover letter PDF file is required" },
	{ name: "turnitin_score_file", message: "Turnitin score file is required" },
];

export const DissertationSubmissionForm = ({ dissertation }: DissertationSubmissionFormProps) => {
	const { setPageTitle } = useGcpsUIController();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const isResubmission = Boolean(dissertation?.resubmission_enabled);

	const form = useForm<DissertationFormValues>({
		resolver: zodResolver(dissertationSubmissionSchema),
		defaultValues: {
			title: "",
			abstract: "",
			dissertation_file: undefined,
			dissertation_docx: undefined,
			proposal_file: undefined,
			// proposal_docx: undefined,
			cover_letter_file: undefined,
			// cover_letter_docx: undefined,
			turnitin_score_file: undefined,
		},
	});

	const { data: filesResp, isLoading: isFilesLoading } = useQuery({
		queryKey: ["dissertation-files"],
		queryFn: getDissertationFiles,
		enabled: !!dissertation?.key,
	});

	const existingFiles = filesResp?.data ?? [];

	const mutation = useMutation({
		mutationFn: async (values: DissertationFormValues) => {
			if (isResubmission && dissertation?.key) {
				return resubmitDissertation(dissertation.key, values);
			}

			return createDissertation(values);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["dissertation-files"] });
			queryClient.invalidateQueries({ queryKey: ["dissertation"] });
			toaster("success", isResubmission ? "Dissertation resubmitted successfully" : "Dissertation submitted successfully");
			navigate(routes.DISSERTATION_DETAILS_PATH);
		},
		onError: handleMutationError,
	});

	useEffect(() => {
		setPageTitle(isResubmission ? "Resubmit Dissertation" : "Submit Dissertation");
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isResubmission]);

	useEffect(() => {
		if (dissertation?.title || dissertation?.abstract) {
			form.reset({
				title: dissertation?.title ?? "",
				abstract: dissertation?.abstract ?? "",
				dissertation_file: undefined,
				dissertation_docx: undefined,
				proposal_file: undefined,
				cover_letter_file: undefined,
				turnitin_score_file: undefined,
			});
		}
	}, [dissertation, form]);

	useEffect(() => {
		if (dissertation && !dissertation.resubmission_enabled) {
			navigate(routes.DISSERTATION_DETAILS_PATH);
		}
	}, [dissertation, navigate]);

	const handleFormSubmit = (values: DissertationFormValues) => {
		if (!isResubmission) {
			let hasMissingFiles = false;

			REQUIRED_FILE_FIELDS.forEach(({ name, message }) => {
				if (!values[name]) {
					hasMissingFiles = true;
					form.setError(name, { type: "required", message });
				}
			});

			if (hasMissingFiles) {
				return;
			}
		}

		mutation.mutate(values);
	};

	return (
		<DashboardLayout>
			<div className="space-y-6 min-w-3xl max-w-3xl m-auto">
				<div>
					<h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
						{isResubmission ? "Resubmit Your Dissertation" : "Submit Your Dissertation"}
					</h1>
					<p className="text-gray-600 dark:text-gray-400">
						{isResubmission
							? "Your previous submission is loaded below. Update the fields you need and upload replacement files only where necessary."
							: "Please provide your dissertation details and upload all required documents."}
					</p>
				</div>

				<Form {...form}>
					<form
						onSubmit={form.handleSubmit(handleFormSubmit)}
						className="space-y-8"
					>
						<div className="bg-white dark:bg-transparent rounded-lg border border-gray-200 dark:border-gray-700 p-6 space-y-6">
							{/* Title Field */}
							<FormField
								control={form.control}
								name="title"
								render={({ field }) => (
									<FormItem>
										<FormControl>
											<Input
												{...field}
												label="Dissertation Title"
												placeholder="Enter your dissertation title"
												disabled={mutation.isPending}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							{/* Abstract Field */}
							<FormField
								control={form.control}
								name="abstract"
								render={({ field }) => (
									<FormItem>
										<FormControl>
											<RichTextEditor
												label="Abstract"
												placeholder="Provide a brief abstract of your dissertation"
												value={field.value}
												onChange={field.onChange}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>

						{isResubmission && (
							<div className="bg-amber-50 border border-amber-200 rounded-lg p-5 space-y-2">
								<h2 className="text-lg font-semibold text-amber-900">Existing Submission</h2>
								<p className="text-sm text-amber-800">
									Your current dissertation record has been prefilled. The files below are available for download, and you can replace only the ones you want to update.
								</p>
							</div>
						)}

						{!!dissertation?.key && (
							<div className="bg-white dark:bg-transparent rounded-lg border border-gray-200 dark:border-gray-700 p-6 space-y-4">
								<div>
									<h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Current Uploaded Files</h2>
									<p className="text-sm text-gray-600 dark:text-gray-400">
										Download your previously submitted files before replacing any of them.
									</p>
								</div>
								{isFilesLoading ? (
									<div className="text-sm text-gray-500">Loading files...</div>
								) : existingFiles.length ? (
									<div className="space-y-3">
										{existingFiles.map((file) => (
											<div
												key={file.name}
												className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-3"
											>
												<div className="min-w-0">
													<p className="truncate text-sm font-medium text-gray-900 dark:text-white">{file.name}</p>
												</div>
												<div className="flex items-center gap-3 shrink-0">
													<a
														href={formFileDownloadByPathUrl(`dissertations/${dissertation.key}/${file.name}`, file.path)}
														className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 hover:underline"
													>
														<ArrowDownToLine className="w-4 h-4" />
														Download
													</a>
												</div>
											</div>
										))}
									</div>
								) : (
									<div className="text-sm text-gray-500">No existing files found for this submission.</div>
								)}
							</div>
						)}

						{/* File Uploads Section */}
						<div className="bg-white dark:bg-transparent rounded-lg border border-gray-200 dark:border-gray-700 p-6 space-y-6">
							<div>
								<h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
									{isResubmission ? "Replacement Documents" : "Required Documents"}
								</h2>
								<p className="text-sm text-gray-600 dark:text-gray-400">
									{isResubmission
										? "Upload only the files you want to replace. Any file you leave empty will keep the current version."
										: "All documents must be uploaded in PDF or Word format."}
								</p>
							</div>
							<div>
								<Label className="block mb-4 font-bold">
									Dissertation File {isResubmission ? "" : "*"}
								</Label>
								<div className="flex gap-4">
									{[
										{ name: "dissertation_file", allowedTypes: ".pdf", subtitle: "PDF Version" },
										{ name: "dissertation_docx", allowedTypes: ".docx, .doc", subtitle: "Word Version" },
									].map(({ name, allowedTypes, subtitle }) => (
										<FormField
											key={name}
											control={form.control}
											name={name as "dissertation_file" | "dissertation_docx"}
											render={({ field: { onChange } }) => (
												<FormItem>
													<FormControl>
														<Input
															type="file"
															allowedTypes={allowedTypes}
															onChange={(e) => onChange(e.target.files?.[0])}
															disabled={mutation.isPending}
															subtitle={subtitle}
															className="h-20 flex-row justify-between items-center"
															iconClassName="text-zinc-300 w-8"
															containerClassName="flex-1"
														/>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>
									))}
								</div>
							</div>
							<Separator orientation="horizontal" className="my-4" />
							<div className="flex justify-between">
								<div>
									<Label className="block mb-4 font-bold">
										Approved Proposal File {isResubmission ? "" : "*"}
									</Label>
									<div className="flex gap-4">
										{/* Proposal File */}
										{[
											{ name: "proposal_file", allowedTypes: ".pdf", subtitle: "PDF Version" },
											// { name: "proposal_docx", allowedTypes: ".docx, .doc", subtitle: "Word Version" },
										].map(({ name, allowedTypes, subtitle }) => (
											<FormField
												key={name}
												control={form.control}
												name={name as "proposal_file"}
												render={({ field: { onChange } }) => (
													<FormItem>
														<FormControl>
															<Input
																type="file"
																allowedTypes={allowedTypes}
																onChange={(e) => onChange(e.target.files?.[0])}
																disabled={mutation.isPending}
																subtitle={subtitle}
																className="h-20 flex-row justify-between items-center"
																iconClassName="text-zinc-300 w-8"
																containerClassName="flex-1"
															/>
														</FormControl>
														<FormMessage />
													</FormItem>
												)}
											/>
										))}
									</div>
								</div>
								<div>
									{/* Cover Letter File */}
									<Label className="block mb-4 font-bold">
										Cover Letter File {isResubmission ? "" : "*"}
									</Label>
									<div className="flex gap-4">
										{[
											{ name: "cover_letter_file", allowedTypes: ".pdf", subtitle: "PDF Version" },
											// { name: "cover_letter_docx", allowedTypes: ".docx, .doc", subtitle: "Word Version" },
										].map(({ name, allowedTypes, subtitle }) => (
											<FormField
												key={name}
												control={form.control}
												name={name as "cover_letter_file"}
												render={({ field: { onChange } }) => (
													<FormItem>
														<FormControl>
															<Input
																type="file"
																allowedTypes={allowedTypes}
																onChange={(e) => onChange(e.target.files?.[0])}
																disabled={mutation.isPending}
																subtitle={subtitle}
																className="h-20 flex-row justify-between items-center"
																iconClassName="text-zinc-300 w-8"
																containerClassName="flex-1"
															/>
														</FormControl>
														<FormMessage />
													</FormItem>
												)}
											/>
										))}
									</div>
								</div>
							</div>
							<Separator orientation="horizontal" className="my-4" />
							{/* Turnitin Score File */}
							<FormField
								control={form.control}
								name="turnitin_score_file"
								render={({ field: { onChange } }) => (
									<FormItem>
										<FormControl>
											<Input
												type="file"
												label={`Turnitin Report of Dissertation ${isResubmission ? "" : "*"}`}
												allowedTypes=".pdf, .docx, .doc, .png, .jpg, .jpeg"
												onChange={(e) => onChange(e.target.files?.[0])}
												disabled={mutation.isPending}
												className="h-20 flex-row justify-between"
												iconClassName="text-zinc-300 w-8"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>

						{/* Submit Button */}
						<div className="flex justify-end gap-3">
							<Button
								type="button"
								variant="outline"
								onClick={() => navigate(routes.DISSERTATION_DETAILS_PATH)}
								disabled={mutation.isPending}
							>
								Cancel
							</Button>
							<Button type="submit" disabled={mutation.isPending} loading={mutation.isPending}>
								{isResubmission ? "Resubmit Dissertation" : "Submit Dissertation"}
							</Button>
						</div>
					</form>
				</Form>
			</div>
		</DashboardLayout>
	);
};
